import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import EditIcon from '@mui/icons-material/Edit';
import { usePositionDialogContent } from './usePositionDialogContent';
import ReviewerAndApproverModal from './ReviewerAndApproverModal';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
    useUpdatePositionDetailsMutation,
    useUpdatePositionsMappedToReviewPolicyMutation,
} from '../../../../api/preHQ';
import { usePositionStatus } from '../../../../hooks/use-position-status';
import { updateAllSelectedPositions } from '../../../../store/slices/positions';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PreviewIcon from '@mui/icons-material/Preview';
import {
    STATUS_IN_DRAFT,
    STATUS_PENDING_APPROVAL,
    STATUS_DRAFT_CHANGE_REQUESTED,
    STATUS_REQUISITION_APPROVED,
    STATUS_REQUISITION_REVIEW,
} from '../../../../constants';

export const allowedPositionActions = (type, actionType, position, data) => {
    if (type === 'bulk') {
        if (
            actionType === 'send_for_approval' &&
            data?.statuses?.every((status) => [STATUS_IN_DRAFT, STATUS_DRAFT_CHANGE_REQUESTED].indexOf(status) >= 0)
        )
            return true;
        return false;
    } else {
        if (
            actionType === 'map-review-policy' &&
            [STATUS_IN_DRAFT, STATUS_PENDING_APPROVAL, STATUS_DRAFT_CHANGE_REQUESTED].indexOf(position?.status) >= 0
        )
            return true;
        if (
            actionType === 'review' &&
            position?.reviewPolicy &&
            [STATUS_PENDING_APPROVAL, STATUS_REQUISITION_REVIEW].indexOf(position?.status) >= 0
        )
            return true;
        if (actionType === 'edit' && [STATUS_IN_DRAFT, STATUS_DRAFT_CHANGE_REQUESTED].indexOf(position?.status) >= 0)
            return true;
        if (
            actionType === 'send_for_approval' &&
            [STATUS_IN_DRAFT, STATUS_DRAFT_CHANGE_REQUESTED].indexOf(position?.status) >= 0
        )
            return true;
        if (actionType === 'create-as-position' && position?.status === STATUS_REQUISITION_APPROVED) return true;
        return false;
    }
};

const PositionMenu = ({ Component, row, handleAction, type = 'single', label }) => {
    const [actionType, setActionType] = useState();
    const [modalType, setModalType] = useState(1);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedPositionReviewId, setSelectedPositionReviewId] = useState(null);

    const handleReviewPolicySuccess = async (success) => {
        setShowReviewModal(false);
        setSelectedPositionReviewId(null);
        if (success) {
            const data = new FormData();
            const selectedPositionIds = type === 'bulk' ? selectedPositions : [row?.id];
            data.append('status', STATUS_REQUISITION_REVIEW);
            data.append('positionId', selectedPositionIds.join(','));
            const response = await updatePositionDetails({
                positionId: selectedPositionIds.join(','),
                payload: data,
            }).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
            handleAction();
        }
    };
    const { selectedPositions } = useSelector((state) => state.positions);
    const [positionStatuses] = usePositionStatus('preHQPositions');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [updatePositionDetails] = useUpdatePositionDetailsMutation();
    const [updatePositionsMappedToReviewPolicy] = useUpdatePositionsMappedToReviewPolicyMutation();

    const handleConfirm = async (payload) => {
        try {
            setActionType();
            const data = new FormData();
            const selectedPositionIds = type === 'bulk' ? selectedPositions : [row?.id];

            if (actionType === 'map-review-policy') {
                data.append('positionIds', selectedPositionIds.join(','));
                data.append('policyId', payload.reviewPolicyId);

                const response = await updatePositionsMappedToReviewPolicy(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'send_for_approval') {
                setShowReviewModal(true);
                setSelectedPositionReviewId(row?.reviewPolicy?.id);
            }
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong', { variant: 'error' });
        } finally {
            if (type === 'bulk')
                dispatch(
                    updateAllSelectedPositions({
                        isChecked: false,
                    }),
                );
        }
    };

    const dialogContent = usePositionDialogContent({
        data: row,
        actionType,
        setActionType,
        modalType,
        setModalType,
        handleConfirm,
        type,
    });

    const actionItems = [
        {
            label: 'Send for Review',
            type: 'send_for_approval',
            icon: <PlaylistAddCheckIcon />,
            onClick: (action) => setActionType(action.type),
            permissions: ['ROLE_BASE_PERMISSION_PRE_HQ_APPROVE_POSITIONS'],
        },
        {
            label: 'Edit',
            type: 'edit',
            icon: <EditIcon />,
            onClick: () => navigate(`/client/positions/details?positionId=${row.id}&action=edit`),
            permissions: ['ROLE_BASE_PERMISSION_PRE_HQ_MODIFY_POSITIONS'],
        },
        {
            label: 'Review',
            type: 'review',
            icon: <PreviewIcon />,
            onClick: () => {
                handleAction('review');
                navigate(`/client/positions/details?positionId=${row.id}&action=review`);
            },
            permissions: ['ROLE_BASE_PERMISSION_PRE_HQ_REVIEW_POSITIONS'],
        },
        {
            label: 'Create as Position',
            type: 'create-as-position',
            icon: <InsertDriveFileIcon />,
            onClick: () => navigate(`/client/approvedrequests/details?positionId=${row.id}&action=edit&draft=true`),
            permissions: ['ROLE_BASE_PERMISSION_PRE_HQ_MOVE_TO_DRAFT_POSITIONS'],
        },
    ];

    const filteredActionItems =
        type === 'bulk'
            ? actionItems.filter((action) =>
                  allowedPositionActions('bulk', action.type, null, { statuses: positionStatuses }),
              )
            : actionItems.filter((action) => allowedPositionActions('single', action.type, row));

    return (
        <>
            <Component
                label={label}
                onDialogClose={() => setActionType()}
                actions={filteredActionItems}
                dialog={dialogContent}
            />
            <ReviewerAndApproverModal
                open={showReviewModal}
                onClose={(success) => handleReviewPolicySuccess(success)}
                positionReviewId={selectedPositionReviewId}
            />
        </>
    );
};

export default PositionMenu;
